<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/mixins";

  .button-container {
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }

  .projects-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: utils.in_rem(8px);
    margin-top: utils.in_rem(16px);

    a {
      @include colors.color(primary_50);
      @include mixins.body2();

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .no-data-wrapper {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    .no-data-text {
      @include colors.color(textGrey);

      h3 {
        @include mixins.h3();
        text-align: center;
      }

      span {
        @include mixins.body2();
        text-align: center;
      }
    }
  }
</style>

<script>
  import Button from "@sourcewhale/shared/svelte-design-system/components/atoms/Button.svelte";
  import Icon from "@sourcewhale/shared/svelte-design-system/components/atoms/Icon.svelte";
  import NoData from "@sourcewhale/shared/svelte-design-system/components/atoms/NoData.svelte";
  import StatusTag from "@sourcewhale/shared/svelte-design-system/components/atoms/TagsAndChips/StatusTag.svelte";
  import DataTable from "@sourcewhale/shared/svelte-design-system/components/molecules/DataTable/DataTable.svelte";
  import { activePopUpModalStore, candidateProjectsStore } from "../stores";
  import { formatDate } from "../utils";

  const { meta } = candidateProjectsStore;

  const columns = [
    {
      name: "projectName",
      title: "Project",
      data: "projectName",
      orderable: true,
      skeletonText: "Loading...",
    },
    {
      name: "relationship",
      title: "Project relationship",
      data: "relationship",
      orderable: true,
      skeletonText: "Loading...",
    },
    {
      name: "creationTime",
      title: "Date Created",
      data: "creationTime",
      orderable: true,
      skeletonText: "Loading...",
      type: "number",
    },
  ];

  let order = $state([{ column: 2, dir: "desc" }]);

  // use frontend sorting as endpoint returns all candidate projects.
  const sortedData = $derived.by(() => {
    if (!order.length) return $candidateProjectsStore;

    const { column, dir } = order[0];
    const key = columns[column]?.name;

    return [...$candidateProjectsStore].sort((a, b) => {
      const x = a[key] || "";
      const y = b[key] || "";

      if (column.type === "number") return dir === "desc" ? y - x : x - y;

      const res = x.toString().localeCompare(y.toString());
      return dir === "desc" ? -res : res;
    });
  });

  const relationshipMap = {
    candidate: "Candidate",
    projectClientContact: "Client Contact",
  };

  const openAddContactToProjectModal = () => {
    activePopUpModalStore.set({ modal: "addContactToProject" });
  };
</script>

<div class="projects-container">
  <div class="button-container">
    <Button onclick={() => openAddContactToProjectModal()}>
      Add To Project
    </Button>
  </div>
  <DataTable
    {columns}
    data={sortedData}
    bind:order
    skeleton={$meta.fetching}
    fetching={$meta.fetching}
    loadingMessage="Loading projects..."
  >
    {#snippet projectName(value, row)}
      <a href={`/project?projectId=${row.projectId}`} target="_blank">
        {value || "Untitled Project"}
      </a>
    {/snippet}

    {#snippet relationship(value)}
      <StatusTag size="medium" kind="neutral">
        {relationshipMap[value] || "Unknown"}
      </StatusTag>
    {/snippet}

    {#snippet creationTime(value)}
      {value ? formatDate(value, { year: "numeric" }) : "-"}
    {/snippet}

    {#snippet noData()}
      <div class="no-data-wrapper">
        <NoData>
          <Icon
            slot="icons"
            class="far fa-briefcase color-textGrey"
            size="xxxl"
          />
          <div slot="text" class="no-data-text">
            <h3>No projects found</h3>
            <span>This candidate is not associated with any projects.</span>
          </div>
        </NoData>
      </div>
    {/snippet}
  </DataTable>
</div>
